import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { selectTheme } from "../features/ui/uiSlice";
import { geocodeAddress } from "../shared/geocode";
import { BISHKEK_LNGLAT, fromLngLat, hasCoords, toLngLat } from "../shared/geo";
import { useYmaps } from "../shared/ymaps";
import YMapsGate from "./YMapsGate";
import "./LocationPicker.css";

/**
 * Карта выбора координат (Яндекс.Карты JS API 3.0).
 *
 * Работаем с императивным API: reactify рассчитан на React 18, а здесь React 19.
 */
function PickerMap({ ymaps3, latitude, longitude, onChange, height, theme, onMapReady }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const schemeRef = useRef(null);
  const markerRef = useRef(null);
  // onChange меняет ссылку на каждый рендер родителя — держим в ref,
  // чтобы это не пересоздавало карту.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Карта создаётся один раз на монтирование.
  useEffect(() => {
    const { YMap, YMapDefaultSchemeLayer, YMapDefaultFeaturesLayer, YMapListener } = ymaps3;
    const started = hasCoords(latitude, longitude);

    const map = new YMap(containerRef.current, {
      location: {
        center: started ? toLngLat(latitude, longitude) : BISHKEK_LNGLAT,
        zoom: started ? 15 : 12,
      },
    });

    const scheme = new YMapDefaultSchemeLayer({ theme });
    map.addChild(scheme);
    map.addChild(new YMapDefaultFeaturesLayer());
    map.addChild(
      new YMapListener({
        layer: "any",
        onClick: (object, event) => {
          // object != null — клик пришёлся по маркеру: его двигает drag, а не клик по карте.
          if (object || !event || !event.coordinates) return;
          const { lat, lng } = fromLngLat(event.coordinates);
          onChangeRef.current(lat, lng);
        },
      })
    );

    mapRef.current = map;
    schemeRef.current = scheme;
    if (onMapReady) onMapReady(map);

    return () => {
      map.destroy();
      mapRef.current = null;
      schemeRef.current = null;
      markerRef.current = null;
      if (onMapReady) onMapReady(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ymaps3]);

  useEffect(() => {
    if (schemeRef.current) schemeRef.current.update({ theme });
  }, [theme]);

  // Маркер следует за пропсами; камера — нет, иначе ручной ввод координат
  // в соседние инпуты дёргал бы карту на каждой набранной цифре.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!hasCoords(latitude, longitude)) {
      if (markerRef.current) {
        map.removeChild(markerRef.current);
        markerRef.current = null;
      }
      return;
    }

    const coordinates = toLngLat(latitude, longitude);
    if (markerRef.current) {
      markerRef.current.update({ coordinates });
      return;
    }

    const pin = document.createElement("div");
    pin.className = "lp-pin";
    markerRef.current = new ymaps3.YMapMarker(
      {
        coordinates,
        draggable: true,
        onDragEnd: (coords) => {
          const { lat, lng } = fromLngLat(coords);
          onChangeRef.current(lat, lng);
        },
      },
      pin
    );
    map.addChild(markerRef.current);
  }, [ymaps3, latitude, longitude]);

  return <div ref={containerRef} className="lp-map" style={{ height }} />;
}

/**
 * Props:
 *   latitude, longitude — текущие значения (number | null)
 *   onChange(lat, lng)  — вызывается при изменении позиции
 *   height              — CSS-высота карты, default '320px'
 */
export default function LocationPicker({ latitude, longitude, onChange, height = "320px" }) {
  const { status, ymaps3, error } = useYmaps();
  const theme = useSelector(selectTheme);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const mapRef = useRef(null);

  const handleSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearchStatus("searching");
    try {
      const found = await geocodeAddress(q);
      if (!found) {
        setSearchStatus("not_found");
        return;
      }
      onChange(found.lat, found.lng);
      if (mapRef.current) {
        mapRef.current.setLocation({
          center: toLngLat(found.lat, found.lng),
          zoom: 16,
          duration: 600,
        });
      }
      setSearchStatus("ok");
    } catch {
      setSearchStatus("error");
    }
  }, [searchQuery, onChange]);

  const handleClear = useCallback(() => onChange(null, null), [onChange]);

  return (
    <div className="lp-container">
      {/* Тулбар доступен всегда — «Сбросить» должно работать, даже если карта не загрузилась. */}
      <div className="lp-toolbar">
        <input
          className="lp-search"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setSearchStatus("");
          }}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Найти адрес на карте…"
        />
        <button type="button" className="lp-btn" onClick={handleSearch}>
          Найти
        </button>
        <button type="button" className="lp-btn lp-btn--danger" onClick={handleClear}>
          Сбросить
        </button>
        {searchStatus === "not_found" && <span className="lp-msg">Не найдено</span>}
        {searchStatus === "error" && <span className="lp-msg lp-msg--error">Ошибка</span>}
      </div>

      <YMapsGate status={status} error={error} height={height}>
        <PickerMap
          ymaps3={ymaps3}
          latitude={latitude}
          longitude={longitude}
          onChange={onChange}
          height={height}
          theme={theme}
          onMapReady={(map) => {
            mapRef.current = map;
          }}
        />
      </YMapsGate>

      <p className="lp-hint">Кликните на карту • Маркер можно перетащить</p>
    </div>
  );
}
